/**
 * Bedarf-Dialog — Bedarf anlegen, bearbeiten und Mitarbeitende zuordnen (Phase 10 von #763, siehe
 * reference/bedarfsplaner/BEDARFSPLANER.md §4 und reference/bedarfsplaner/Dialoge Reference.html).
 * Immer auf genau EINE Bedarfszeile (Posten × Tag × Zeitfenster) bezogen.
 *
 * Zwei Einstiege:
 * - oeffneNeuenBedarf(tag): Anlegen-Modus — nur das Bedarf-Formular; nach dem Speichern geht es im
 *   selben Dialog mit der Zuordnung weiter.
 * - oeffneBedarfZuordnung(bedarfID): Zuordnungs-Modus — oben der Bedarf (Zusammenfassung, per Stift
 *   bearbeitbar), darunter Zugeordnet, Vorschläge, "Anderer Posten — Umsetzung nötig" und nicht verfügbar.
 *
 * Änderungen am Bedarf reicht der Server an bereits zugeordnete Schichten weiter (nur, wo die Schicht noch
 * den alten Wert trägt) — nicht angepasste Schichten werden im Dialog aufgelistet. Solange Mitarbeitende
 * zugeordnet sind, ist der Posten gesperrt; Löschen verlangt dann eine zweite Bestätigung (die Schichten
 * werden mitgelöscht). Event-Bedarfe sind hier bearbeitbar, aber nur im Event-Editor löschbar.
 *
 * Baut das Modal (`.modal` Backdrop + `.dlg` Karte, wie die Editoren) selbst auf und hängt es an den
 * Body — bewusst getrennt vom Schicht-Editor-Modal (#my-modal) und der SystemDialogService-Move-by-ID-
 * Mechanik, um Kopplung zu vermeiden.
 */

import dayjs from '../../../../shared/services/dayjs.js';
import { bedarfService } from '../services/BedarfService.js';
import { stammdatenService } from '../services/StammdatenService.js';
import { zeigeKleineNachricht } from '../systemNachricht.js';
import { bestaetige, istAppDialogOffen } from './bestaetigung.js';
import {
	toIso,
	baueStepper,
	bauePostenSelect,
	postenInfo,
	postenGesperrtHinweis,
	baueZeitInput,
	feld,
	textInput,
	textArea,
	baueSelect,
} from '../components/bedarfFelder.js';
import { secureHTML, convertNumberDE } from '../util.js';

let onChangeCallback = null;
let onEventOeffnenCallback = null;
let aktuellerBedarfID = null;
// Anlegen-Modus: Tag (YYYY-MM-DD) des neuen Bedarfs; null im Zuordnungs-Modus.
let neuerTag = null;
let bearbeiten = false;
let entwurf = null;
// Ergebnis der letzten Weitergabe an zugeordnete Schichten (nicht angepasste Schichten anzeigen).
let letzterBericht = null;
// Sortierung der Vorschläge ('empfohlen' | 'stunden' | 'kosten'); bleibt bewusst über Dialoge hinweg erhalten.
let vorschlagSortierung = 'empfohlen';
let stammdaten ={ bereiche: [], postenListe: [], qualifikationen: [], einsatzorte: [] };
let escHandler = null;

function initialen(vorname, nachname) {
	return `${(vorname || '').charAt(0)}${(nachname || '').charAt(0)}`.toUpperCase() || '–';
}

function schliesse() {
	document.getElementById('bedarf-zuordnung-modal')?.remove();
	if (escHandler) {
		document.removeEventListener('keydown', escHandler);
		escHandler = null;
	}
}

/** Baut die leere Modal-Hülle (Backdrop + .dlg mit violettem Header) und hängt sie an den Body. */
function baueModalHuelle() {
	schliesse();
	const modal = document.createElement('div');
	modal.className = 'modal';
	modal.id = 'bedarf-zuordnung-modal';
	modal.style.display = 'block';
	modal.innerHTML = `
		<div class="modal-inhalt" style="width:560px;max-height:84vh;">
			<div class="dlg" style="max-height:84vh;">
				<div class="dlg-head accent">
					<div class="ic"><i class="bi bi-clipboard2-check"></i></div>
					<div style="min-width:0;">
						<div class="ttl" id="bz-titel"></div>
						<div class="sub" id="bz-sub"></div>
					</div>
					<i class="bi bi-x-lg x" id="bz-close"></i>
				</div>
				<div class="dlg-body" id="bz-body"></div>
				<div class="dlg-foot" id="bz-foot"></div>
			</div>
		</div>`;
	document.body.appendChild(modal);
	// Schließen per Backdrop-Klick, X-Button und Escape.
	modal.addEventListener('mousedown', (e) => { if (e.target === modal) schliesse(); });
	modal.querySelector('#bz-close').addEventListener('click', schliesse);
	// Escape schließt nicht, solange darüber eine Bestätigung offen ist — die schließt Escape selbst.
	escHandler = (e) => { if (e.key === 'Escape' && !istAppDialogOffen()) schliesse(); };
	document.addEventListener('keydown', escHandler);
	return modal;
}

async function ladeStammdaten() {
	const unternehmen = await stammdatenService.getUnternehmensdaten();
	stammdaten = {
		bereiche: (unternehmen?.Bereiche || []).filter((b) => b.Verwenden),
		postenListe: await stammdatenService.getPosten(),
		qualifikationen: await stammdatenService.getQualifikationen(),
		einsatzorte: await stammdatenService.getEinsatzorte(),
	};
}

// --- Bedarf-Formular (Anlegen / Bearbeiten) ---

/** Formular-Entwurf aus einer gespeicherten Bedarfszeile (oder leer für einen neuen Bedarf). */
function erstelleEntwurf(bedarf) {
	if (!bedarf) {
		const erstePosten = stammdaten.postenListe[0];
		const info = postenInfo(stammdaten.bereiche, stammdaten.postenListe, erstePosten?._id);
		return {
			tag: neuerTag,
			// Vorbelegt mit dem Posten-Namen; folgt dem Posten, solange er nicht von Hand geändert wurde.
			Titel: info.posten,
			PostenID: info.postenID || '',
			Posten: info.posten,
			BereichID: info.bereichID,
			Bereich: info.bereich,
			von: '17:00',
			bis: '23:00',
			anzahl: 1,
			QualifikationID: '',
			EinsatzortID: '',
			SchichtNotiz: '',
		};
	}
	return {
		tag: dayjs(bedarf.Datum).format('YYYY-MM-DD'),
		// Ohne gespeicherten Titel zeigt das Feld den Posten-Namen — denselben Rückfall nutzt der Server für
		// die Schicht (SchichtService._bedarfTitel).
		Titel: bedarf.Titel || bedarf.Posten || '',
		PostenID: bedarf.PostenID,
		Posten: bedarf.Posten,
		BereichID: bedarf.BereichID,
		Bereich: bedarf.Bereich,
		von: dayjs(bedarf.SchichtVon).format('HH:mm'),
		bis: dayjs(bedarf.SchichtBis).format('HH:mm'),
		anzahl: bedarf.BedarfAnzahl,
		QualifikationID: bedarf.QualifikationID || '',
		EinsatzortID: bedarf.EinsatzortID || '',
		SchichtNotiz: bedarf.SchichtNotiz || '',
	};
}

/**
 * Payload für POST/PUT aus dem Entwurf; unveränderte Felder (Farbe, EventID) bleiben erhalten. Der Titel
 * eines Event-Bedarfs kommt vom Event (der Event-Editor überschreibt ihn beim Speichern) und bleibt daher
 * unangetastet.
 */
function baueBedarfPayload(bedarf) {
	const quali = stammdaten.qualifikationen.find((q) => q._id === entwurf.QualifikationID);
	const payload = {
		BetriebsstaetteID: bedarf?.BetriebsstaetteID || stammdatenService.aktuelleBetriebsstaette,
		BereichID: entwurf.BereichID,
		Bereich: entwurf.Bereich,
		PostenID: entwurf.PostenID,
		Posten: entwurf.Posten,
		QualifikationID: entwurf.QualifikationID || '',
		Qualifikation: quali?.BezeichnungNeutral || '',
		Datum: `${entwurf.tag}T00:00:00`,
		SchichtVon: toIso(entwurf.tag, entwurf.von),
		SchichtBis: toIso(entwurf.tag, entwurf.bis, true, entwurf.von),
		BedarfAnzahl: entwurf.anzahl,
		SchichtNotiz: entwurf.SchichtNotiz,
		Titel: bedarf?.EventID ? (bedarf.Titel || '') : (entwurf.Titel || '').trim(),
		SchichtFarbe: bedarf?.SchichtFarbe || '',
	};
	// Einsatzort eines Event-Bedarfs kommt vom Event (der Event-Editor setzt ihn beim Speichern neu).
	if (bedarf?.EventID) {
		payload.EventID = bedarf.EventID;
		payload.EinsatzortID = bedarf.EinsatzortID || '';
		payload.Einsatzort = bedarf.Einsatzort || '';
	} else {
		payload.EinsatzortID = entwurf.EinsatzortID || '';
		payload.Einsatzort = stammdaten.einsatzorte.find((e) => e._id === entwurf.EinsatzortID)?.Bezeichnung || '';
	}
	return payload;
}

/**
 * Das Bedarf-Formular. Posten gesperrt und Anzahl-Minimum = Zugeordnete, solange Mitarbeitende
 * zugeordnet sind (serverseitig ebenso geprüft).
 * @param {object|null} bedarf gespeicherter Bedarf (null im Anlegen-Modus)
 * @param {number} filled Anzahl bereits Zugeordneter
 * @param {array} zugeordnet zugeordnete Kandidaten (für die Bestätigungstexte)
 */
function baueFormular(bedarf, filled, zugeordnet) {
	const form = document.createElement('div');
	form.className = 'bz-form';

	// Titel — wird zum Titel der Schicht der zugeordneten Mitarbeitenden. Bei Event-Bedarfen kommt er vom
	// Event (gesperrt, sonst überschreibt ihn der Event-Editor beim nächsten Speichern ohnehin).
	const titelInput = textInput(entwurf.Titel, (v) => { entwurf.Titel = v; }, entwurf.Posten || 'Titel der Schicht');
	const titelFeld = feld('Titel', titelInput);
	if (bedarf?.EventID) {
		titelInput.disabled = true;
		const hinweis = document.createElement('div');
		hinweis.className = 'lockhint';
		hinweis.innerHTML = '<i class="bi bi-stars"></i>vom Event — Titel im Event-Editor ändern';
		titelFeld.appendChild(hinweis);
	}
	form.appendChild(titelFeld);

	const postenFeld = feld('Posten', bauePostenSelect({
		bereiche: stammdaten.bereiche,
		postenListe: stammdaten.postenListe,
		postenID: entwurf.PostenID,
		gesperrt: filled > 0,
		className: 'inp',
		onChange: (info) => {
			// Titel folgt dem Posten, solange er noch dem bisherigen Posten-Namen entspricht (oder leer ist).
			if (!bedarf?.EventID && (!entwurf.Titel.trim() || entwurf.Titel === entwurf.Posten)) {
				entwurf.Titel = info.posten;
				titelInput.value = info.posten;
			}
			titelInput.placeholder = info.posten || 'Titel der Schicht';
			entwurf.PostenID = info.postenID;
			entwurf.Posten = info.posten;
			entwurf.BereichID = info.bereichID;
			entwurf.Bereich = info.bereich;
		},
	}));
	if (filled > 0) postenFeld.appendChild(postenGesperrtHinweis(filled));
	const reihe1 = document.createElement('div');
	reihe1.className = 'row2';
	reihe1.append(postenFeld, feld('Anzahl', baueStepper({ wert: entwurf.anzahl, min: Math.max(1, filled), onChange: (n) => { entwurf.anzahl = n; } })));
	form.appendChild(reihe1);

	const reihe2 = document.createElement('div');
	reihe2.className = 'row2';
	reihe2.append(
		feld('Von', baueZeitInput(entwurf.von, (v) => { entwurf.von = v; }, 'inp')),
		feld('Bis', baueZeitInput(entwurf.bis, (v) => { entwurf.bis = v; }, 'inp')),
	);
	form.appendChild(reihe2);

	const reihe3 = document.createElement('div');
	reihe3.className = 'row2';
	reihe3.appendChild(feld('Qualifikation', baueSelect(
		stammdaten.qualifikationen.map((q) => ({ value: q._id, text: q.BezeichnungNeutral })),
		entwurf.QualifikationID,
		'– keine Vorgabe –',
		(v) => { entwurf.QualifikationID = v; },
	)));
	if (!bedarf?.EventID) {
		reihe3.appendChild(feld('Einsatzort', baueSelect(
			stammdaten.einsatzorte.map((e) => ({ value: e._id, text: e.Bezeichnung })),
			entwurf.EinsatzortID,
			'– kein Einsatzort –',
			(v) => { entwurf.EinsatzortID = v; },
		)));
	}
	form.appendChild(reihe3);

	form.appendChild(feld('Notiz', textArea(entwurf.SchichtNotiz, (v) => { entwurf.SchichtNotiz = v; })));

	const aktionen = document.createElement('div');
	aktionen.className = 'bz-form-akt';
	const abbrechen = document.createElement('button');
	abbrechen.className = 'dbtn';
	abbrechen.textContent = 'Abbrechen';
	abbrechen.addEventListener('click', () => {
		if (!bedarf) {
			schliesse();
			return;
		}
		bearbeiten = false;
		rendereInhalt();
	});
	const speichern = document.createElement('button');
	speichern.className = 'dbtn primary';
	speichern.innerHTML = '<i class="bi bi-check-lg"></i>Speichern';
	speichern.addEventListener('click', async () => {
		speichern.disabled = true;
		await speichereBedarf(bedarf, filled, zugeordnet);
		speichern.disabled = false;
	});
	aktionen.append(abbrechen, speichern);
	form.appendChild(aktionen);
	return form;
}

async function speichereBedarf(bedarf, filled, zugeordnet) {
	if (!entwurf.PostenID) {
		zeigeKleineNachricht('Bitte einen Posten wählen.', 0);
		return;
	}
	if (!entwurf.von || !entwurf.bis || entwurf.von === entwurf.bis) {
		zeigeKleineNachricht('Bitte ein gültiges Zeitfenster angeben.', 0);
		return;
	}
	const payload = baueBedarfPayload(bedarf);

	if (!bedarf) {
		const neu = await bedarfService.erstelleBedarf(payload);
		if (!neu) return;
		zeigeKleineNachricht('Bedarf angelegt', 1);
		aktuellerBedarfID = neu._id;
		neuerTag = null;
		bearbeiten = false;
		if (typeof onChangeCallback === 'function') onChangeCallback({ art: 'bedarf', schichtenGeaendert: false });
		await rendereInhalt();
		return;
	}

	// Zugeordnete Schichten werden mit angepasst — vorher bestätigen lassen.
	const titelVorher = bedarf.Titel || bedarf.Posten || '';
	const titelNachher = payload.Titel || payload.Posten || '';
	const weitergabeRelevant = !dayjs(payload.SchichtVon).isSame(dayjs(bedarf.SchichtVon))
		|| !dayjs(payload.SchichtBis).isSame(dayjs(bedarf.SchichtBis))
		|| titelVorher !== titelNachher
		|| payload.SchichtNotiz !== (bedarf.SchichtNotiz || '')
		|| payload.EinsatzortID !== (bedarf.EinsatzortID || '');
	if (filled > 0 && weitergabeRelevant) {
		const namen = zugeordnet.map((k) => `${k.Vorname} ${k.Nachname}`).join(', ');
		const ok = await bestaetige({
			titel: 'Zugeordnete Schichten anpassen?',
			text: `Die Schichten von ${namen} werden an den geänderten Bedarf angepasst — außer dort, wo sie von Hand geändert wurden.`,
			okText: 'Speichern',
		});
		if (!ok) return;
	}
	const ergebnis = await bedarfService.updateBedarf(bedarf._id, payload);
	if (!ergebnis) return;
	letzterBericht = ergebnis.schichten;
	const { aktualisiert } = ergebnis.schichten;
	zeigeKleineNachricht(aktualisiert > 0 ? `Bedarf gespeichert · ${aktualisiert} Schicht(en) angepasst` : 'Bedarf gespeichert', 1);
	bearbeiten = false;
	// Wurden zugeordnete Schichten mit angepasst, muss das Raster sie neu zeigen; sonst reichen die Bedarfszeilen.
	if (typeof onChangeCallback === 'function') onChangeCallback({ art: 'bedarf', schichtenGeaendert: aktualisiert > 0 });
	await rendereInhalt();
}

/** Zusammenfassung des Bedarfs (Lese-Modus) mit Stift zum Bearbeiten. */
function baueZusammenfassung(bedarf) {
	const karte = document.createElement('div');
	karte.className = 'bdef';
	const von = dayjs(bedarf.SchichtVon).format('HH:mm');
	const bis = dayjs(bedarf.SchichtBis).format('HH:mm');
	// Titel der Schicht vorne (ohne eigenen Titel = Posten-Name, wie auf dem Server). Freitexte escapen.
	const titel = bedarf.Titel || bedarf.Posten || '—';
	const teile = [`<b>${secureHTML(titel)}</b>`];
	if (bedarf.Titel && bedarf.Titel !== bedarf.Posten) teile.push(secureHTML(bedarf.Posten || '—'));
	teile.push(`<i class="bi bi-clock"></i>${von}–${bis}`, `<i class="bi bi-people"></i>${bedarf.BedarfAnzahl}`);
	if (bedarf.Qualifikation) teile.push(`<i class="bi bi-award"></i>${secureHTML(bedarf.Qualifikation)}`);
	if (bedarf.Einsatzort) teile.push(`<i class="bi bi-geo-alt"></i>${secureHTML(bedarf.Einsatzort)}`);
	karte.innerHTML = `<div class="bdef-info">${teile.join('<span class="sep">·</span>')}${bedarf.SchichtNotiz ? `<div class="bdef-notiz">${secureHTML(bedarf.SchichtNotiz)}</div>` : ''}</div>`;
	const stift = document.createElement('button');
	stift.className = 'dbtn';
	stift.title = 'Bedarf bearbeiten';
	stift.innerHTML = '<i class="bi bi-pencil"></i>Bearbeiten';
	stift.addEventListener('click', () => {
		bearbeiten = true;
		entwurf = erstelleEntwurf(bedarf);
		rendereInhalt();
	});
	karte.appendChild(stift);
	return karte;
}

/** Hinweis auf zugeordnete Schichten, an die die letzte Änderung nicht weitergereicht wurde. */
function baueBericht(zugeordnet) {
	if (!letzterBericht) return null;
	const nichtAngepasst = [
		...letzterBericht.abweichend.map((a) => ({ ...a, grund: `von Hand geändert (${a.Felder.join(', ')})` })),
		...letzterBericht.fehler.map((f) => ({ ...f, grund: f.Grund })),
	];
	if (nichtAngepasst.length === 0) return null;
	const nameVon = (schichtID) => {
		const k = zugeordnet.find((z) => z.SchichtID === schichtID);
		return k ? `${k.Vorname} ${k.Nachname}` : 'Schicht';
	};
	const box = document.createElement('div');
	box.className = 'bz-bericht';
	box.innerHTML = `<div><i class="bi bi-exclamation-triangle"></i><b>Nicht an alle Schichten weitergegeben</b></div>
		<ul>${nichtAngepasst.map((n) => `<li>${nameVon(n.SchichtID)} — ${n.grund}</li>`).join('')}</ul>
		<div class="hint">Diese Schichten bitte im Dienstplan prüfen.</div>`;
	return box;
}

/** Footer: Löschen — oder bei Event-Bedarfen der Verweis auf den Event-Editor. */
function rendereFooter(bedarf, zugeordnet) {
	const foot = document.getElementById('bz-foot');
	foot.innerHTML = '';
	if (!bedarf) {
		foot.style.display = 'none';
		return;
	}
	foot.style.display = '';
	if (bedarf.EventID) {
		const hinweis = document.createElement('span');
		hinweis.className = 'bz-eventhint';
		hinweis.innerHTML = `<i class="bi bi-stars"></i>Teil des Events${bedarf.Titel ? ` „${bedarf.Titel}“` : ''} — löschen im Event-Editor`;
		foot.appendChild(hinweis);
		if (typeof onEventOeffnenCallback === 'function') {
			const sp = document.createElement('div');
			sp.className = 'sp';
			const link = document.createElement('button');
			link.className = 'dbtn';
			link.innerHTML = '<i class="bi bi-box-arrow-up-right"></i>Event öffnen';
			link.addEventListener('click', () => {
				const eventID = bedarf.EventID;
				schliesse();
				onEventOeffnenCallback(eventID);
			});
			foot.append(sp, link);
		}
		return;
	}
	const loeschen = document.createElement('button');
	loeschen.className = 'dbtn danger';
	loeschen.innerHTML = '<i class="bi bi-trash"></i>Bedarf löschen';
	loeschen.addEventListener('click', async () => {
		if (!await bestaetige({ titel: 'Bedarf löschen?', okText: 'Löschen' })) return;
		// Mit Zuordnungen: zweite Bestätigung, dass die Schichten mitgelöscht werden — sonst bleibt es blockiert.
		const mitZuordnung = zugeordnet.length > 0;
		if (mitZuordnung) {
			const namen = zugeordnet.map((k) => `${k.Vorname} ${k.Nachname}`).join(', ');
			const ok = await bestaetige({
				titel: 'Zugeordnete Schichten ebenfalls löschen?',
				text: `Folgende Schichten werden ebenfalls gelöscht: ${namen}.\nDie Mitarbeitenden werden bei freigegebenen Schichten benachrichtigt.`,
				okText: 'Alles löschen',
			});
			if (!ok) return;
		}
		loeschen.disabled = true;
		const ok = await bedarfService.entferneBedarf(bedarf._id, mitZuordnung);
		if (!ok) {
			loeschen.disabled = false;
			return;
		}
		zeigeKleineNachricht('Bedarf gelöscht', 1);
		if (typeof onChangeCallback === 'function') onChangeCallback({ art: 'bedarf', schichtenGeaendert: mitZuordnung });
		schliesse();
	});
	foot.appendChild(loeschen);
}

// --- Zuordnung ---

/** Eine Kandidatenzeile (.macand) mit Avatar, Name, Personalnummer, Match-Badges und Aktionsbutton. */
function baueKandidatZeile(kandidat, aktionEl, { done = false, gedimmt = false } = {}) {
	const zeile = document.createElement('div');
	zeile.className = `macand${done ? ' done' : ''}`;
	if (gedimmt) zeile.style.opacity = '.6';

	const avatar = document.createElement('div');
	avatar.className = 'avatar av';
	avatar.textContent = initialen(kandidat.Vorname, kandidat.Nachname);
	zeile.appendChild(avatar);

	const info = document.createElement('div');
	info.style.minWidth = '0';
	// Wie die Statuszelle: geplante Stunden des Abrechnungsmonats / Monatssoll
	const stunden = kandidat.Sollstunden > 0 ? `${Math.round(kandidat.StundenGeplant)}/${Math.round(kandidat.Sollstunden)} h` : '';
	// AG-Kosten kommen vom Server nur für Rollen mit Budget-Einsicht
	const agKosten = kandidat.AGKostenProStunde !== undefined
		? `<span title="Geschätzte Arbeitgeberkosten pro Stunde">${convertNumberDE(kandidat.AGKostenProStunde, 2)} €/h</span>` : '';
	info.innerHTML = `
		<div class="nm">${kandidat.Vorname} ${kandidat.Nachname}</div>
		<div class="pnr">#${kandidat.Personalnummer}</div>
		<div class="mt">${baueBadges(kandidat)}${stunden ? `<span title="geplant im Abrechnungsmonat / Monatssoll">${stunden}</span>` : ''}${agKosten}</div>`;
	zeile.appendChild(info);

	const act = document.createElement('div');
	act.className = 'act';
	act.appendChild(aktionEl);
	zeile.appendChild(act);
	return zeile;
}

/** Match-Badges je nach Kategorie/Signal (Qualifikation, Wunschzeit, Posten, Grund). */
function baueBadges(kandidat) {
	const badges = [];
	if (kandidat.Kategorie === 'unavailable') {
		const icon = kandidat.Grund === 'Abwesend' ? 'bi-airplane' : 'bi-slash-circle';
		badges.push(`<span class="match warn"><i class="bi ${icon}"></i>${kandidat.Grund}</span>`);
		return badges.join('');
	}
	if (kandidat.Kategorie === 'anderer_posten') {
		badges.push(`<span class="match warn"><i class="bi bi-exclamation-triangle"></i>Posten ${kandidat.AktuellerPosten || '—'}</span>`);
		return badges.join('');
	}
	if (kandidat.Wunsch === 'positiv') {
		badges.push('<span class="match good"><i class="bi bi-hand-thumbs-up"></i>Wunschzeit</span>');
	}
	if (kandidat.Qualifikation) {
		badges.push(`<span class="match ok">${kandidat.Qualifikation}</span>`);
	}
	if (kandidat.NearCap) {
		badges.push('<span class="match warn"><i class="bi bi-exclamation-triangle"></i>Stunden fast voll</span>');
	}
	return badges.join('');
}

/**
 * Sortiert die Vorschläge (Kopie): 'empfohlen' = Reihenfolge vom Server, 'stunden' = meiste freie Stunden
 * (Soll − geplant) zuerst, ohne Monatssoll ans Ende, 'kosten' = günstigste AG-Kosten/h zuerst. Gleichstand nach Name.
 */
function sortiereVorschlaege(vorschlaege, sortierung) {
	if (sortierung === 'empfohlen') return [...vorschlaege];
	const name = (k) => `${k.Nachname}${k.Vorname}`;
	const wert = sortierung === 'stunden'
		? (k) => (k.Sollstunden > 0 ? -(k.Sollstunden - (k.StundenGeplant || 0)) : Infinity)
		: (k) => k.AGKostenProStunde ?? Infinity;
	return [...vorschlaege].sort((a, b) => (wert(a) - wert(b)) || name(a).localeCompare(name(b)));
}

function sektionKopf(text) {
	const el = document.createElement('div');
	el.className = 'zp-sec';
	el.textContent = text;
	return el;
}

/**
 * Nach einer Zuordnungs-Aktion: Meldung, Ansicht über die Änderung informieren und den Dialog neu laden.
 * @param {object|false} aenderung was sich geändert hat (für ein gezieltes Update statt Neuladen), z.B.
 *   { art: 'zugeordnet', schicht } · { art: 'entfernt', schicht } · { art: 'umgesetzt', quelle, ziel };
 *   false/leer = Aktion fehlgeschlagen
 * @param {string} meldung
 */
async function nachAktion(aenderung, meldung) {
	if (!aenderung) return;
	if (meldung) zeigeKleineNachricht(meldung, 1);
	letzterBericht = null;
	if (typeof onChangeCallback === 'function') onChangeCallback(aenderung);
	await rendereInhalt();
}

function rendereKandidaten(body, { need, filled, zugeordnet, vorschlaege, andererPosten, unavailable }) {
	// Zugeordnet
	body.appendChild(sektionKopf(`Zugeordnet (${filled} von ${need})`));
	if (zugeordnet.length === 0) {
		const leer = document.createElement('div');
		leer.className = 'macand';
		leer.style.cssText = 'border-style:dashed;justify-content:center;color:var(--muted-2);font-size:12.5px;font-weight:600;padding:14px;';
		leer.textContent = 'Noch niemand auf diesem Posten';
		body.appendChild(leer);
	} else {
		zugeordnet.forEach((k) => {
			const btn = document.createElement('button');
			btn.className = 'assignbtn';
			btn.style.cssText = 'background:transparent;color:var(--pos);';
			btn.innerHTML = '<i class="bi bi-x-lg"></i>Entfernen';
			btn.addEventListener('click', async () => {
				btn.disabled = true;
				const schicht = await bedarfService.entferneZuordnung(k.SchichtID);
				await nachAktion(schicht && { art: 'entfernt', schicht }, 'Zuordnung entfernt');
			});
			body.appendChild(baueKandidatZeile(k, btn, { done: true }));
		});
	}

	// Vorschläge (sortierbar, siehe sortiereVorschlaege)
	if (vorschlaege.length > 0) {
		const mitKosten = vorschlaege.some((k) => k.AGKostenProStunde !== undefined);
		if (vorschlagSortierung === 'kosten' && !mitKosten) vorschlagSortierung = 'empfohlen';
		const liste = document.createElement('div');
		liste.className = 'zp-list';
		const fuelleListe = () => {
			liste.replaceChildren(...sortiereVorschlaege(vorschlaege, vorschlagSortierung).map((k) => {
				const btn = document.createElement('button');
				btn.className = 'assignbtn';
				btn.innerHTML = '<i class="bi bi-plus-lg"></i>Zuweisen';
				btn.addEventListener('click', async () => {
					btn.disabled = true;
					const schicht = await bedarfService.zuordneMitarbeiter(aktuellerBedarfID, k.MitarbeiterID, k.QualifikationID, k.Qualifikation);
					await nachAktion(schicht && { art: 'zugeordnet', schicht }, 'Mitarbeiter zugewiesen');
				});
				return baueKandidatZeile(k, btn);
			}));
		};

		const kopf = sektionKopf('Vorschläge — verfügbar');
		kopf.classList.add('mit-sort');
		const optionen = [['empfohlen', 'Empfohlen'], ['stunden', 'Freie Std.'], ...(mitKosten ? [['kosten', 'AG €/h']] : [])];
		const seg = document.createElement('div');
		seg.className = 'seg zp-sort';
		optionen.forEach(([wert, text]) => {
			const o = document.createElement('div');
			o.className = `o${wert === vorschlagSortierung ? ' on' : ''}`;
			o.textContent = text;
			o.addEventListener('click', () => {
				vorschlagSortierung = wert;
				seg.querySelectorAll('.o').forEach((el) => el.classList.toggle('on', el === o));
				fuelleListe();
			});
			seg.appendChild(o);
		});
		kopf.appendChild(seg);
		body.append(kopf, liste);
		fuelleListe();
	}

	// Anderer Posten — Umsetzung nötig
	if (andererPosten.length > 0) {
		body.appendChild(sektionKopf('Anderer Posten — Umsetzung nötig'));
		andererPosten.forEach((k) => {
			const btn = document.createElement('button');
			btn.className = 'assignbtn';
			btn.style.cssText = 'background:var(--tint-2);color:var(--muted);';
			btn.innerHTML = '<i class="bi bi-arrow-left-right"></i>Umsetzen';
			btn.addEventListener('click', async () => {
				btn.disabled = true;
				const ergebnis = await bedarfService.umsetzeSchicht(aktuellerBedarfID, k.SchichtID);
				await nachAktion(ergebnis && { art: 'umgesetzt', quelle: ergebnis.quelle, ziel: ergebnis.ziel }, 'Schicht umgesetzt');
			});
			body.appendChild(baueKandidatZeile(k, btn));
		});
	}

	// Nicht verfügbar
	if (unavailable.length > 0) {
		body.appendChild(sektionKopf('Nicht verfügbar'));
		unavailable.forEach((k) => {
			const btn = document.createElement('button');
			btn.className = 'assignbtn';
			btn.style.cssText = 'background:var(--tint-2);color:var(--muted-2);';
			btn.disabled = true;
			btn.innerHTML = '<i class="bi bi-slash-circle"></i>';
			body.appendChild(baueKandidatZeile(k, btn, { gedimmt: true }));
		});
	}
}

/** Anlegen-Modus: nur Kopf + Formular. */
function rendereAnlegen() {
	document.getElementById('bz-titel').textContent = 'Neuer Bedarf';
	document.getElementById('bz-sub').innerHTML = `<i class="bi bi-calendar3"></i>${dayjs(neuerTag).format('dd, DD.MM.YYYY')}`;
	const body = document.getElementById('bz-body');
	body.innerHTML = '';
	body.appendChild(baueFormular(null, 0, []));
	const hinweis = document.createElement('div');
	hinweis.className = 'bz-hint';
	hinweis.innerHTML = '<i class="bi bi-info-circle"></i>Nach dem Speichern können direkt Mitarbeitende zugewiesen werden.';
	body.appendChild(hinweis);
	rendereFooter(null, []);
}

/** Lädt die Kandidaten und rendert Kopf + Body des Dialogs neu. */
async function rendereInhalt() {
	if (neuerTag) {
		rendereAnlegen();
		return;
	}
	const daten = await bedarfService.holeKandidaten(aktuellerBedarfID);
	if (!daten) {
		schliesse();
		return;
	}
	const { Bedarf: bedarf, Need: need, Filled: filled, Kandidaten: kandidaten } = daten;

	document.getElementById('bz-titel').textContent = `Bedarf: ${bedarf.Bereich || '—'} · ${bedarf.Posten || '—'}`;
	const von = dayjs(bedarf.SchichtVon).format('HH:mm');
	const bis = dayjs(bedarf.SchichtBis).format('HH:mm');
	const datum = dayjs(bedarf.Datum).format('dd, DD.MM.');
	document.getElementById('bz-sub').innerHTML = `
		<i class="bi bi-clock"></i>${von}–${bis}
		<span style="opacity:.5;">·</span><i class="bi bi-calendar3"></i>${datum}
		<span style="opacity:.5;">·</span><i class="bi bi-geo-alt"></i>${bedarf.Bereich || '—'}`;

	const zugeordnet = kandidaten.filter((k) => k.Kategorie === 'zugeordnet');
	const body = document.getElementById('bz-body');
	body.innerHTML = '';

	// Bedarf-Definition: Zusammenfassung oder Formular.
	body.appendChild(sektionKopf('Bedarf'));
	body.appendChild(bearbeiten ? baueFormular(bedarf, filled, zugeordnet) : baueZusammenfassung(bedarf));
	const bericht = baueBericht(zugeordnet);
	if (bericht) body.appendChild(bericht);

	// Fortschrittsring (conic-gradient, --pos wenn vollständig, sonst --amber).
	const prozent = need > 0 ? Math.min(100, Math.round((filled / need) * 100)) : 0;
	const ringFarbe = filled >= need ? 'var(--pos)' : 'var(--amber)';
	const prog = document.createElement('div');
	prog.className = 'zp-prog';
	prog.innerHTML = `
		<div class="ring ringbig" style="background:conic-gradient(${ringFarbe} ${prozent}%,var(--tint-2) 0);"><div class="hole">${filled}/${need}</div></div>
		<div>
			<div class="pt">${filled} von ${need} Posten <b>${bedarf.Posten || ''}</b> besetzt</div>
			<div class="ps">${bedarf.Bereich || ''} · ${bedarf.Posten || ''}${bedarf.QualifikationID ? ' — Vorschläge auf die geforderte Qualifikation gefiltert' : ''}</div>
		</div>`;
	body.appendChild(prog);

	rendereKandidaten(body, {
		need,
		filled,
		zugeordnet,
		vorschlaege: kandidaten.filter((k) => k.Kategorie === 'vorschlag'),
		andererPosten: kandidaten.filter((k) => k.Kategorie === 'anderer_posten'),
		unavailable: kandidaten.filter((k) => k.Kategorie === 'unavailable'),
	});
	rendereFooter(bedarf, zugeordnet);
}

/**
 * Öffnet den Bedarf-Dialog für eine bestehende Bedarfszeile (Zuordnung + Bearbeiten).
 * @param {string} bedarfID
 * @param {function} onChange (aenderung) => void nach Änderungen — aenderung beschreibt, was sich geändert
 *   hat (siehe nachAktion bzw. { art: "bedarf", schichtenGeaendert }), damit die Ansicht gezielt aktualisieren kann
 * @param {object} [optionen]
 * @param {function} [optionen.onEventOeffnen] (eventID) => void — "Event öffnen" bei Event-Bedarfen
 */
async function oeffneBedarfZuordnung(bedarfID, onChange, { onEventOeffnen = null } = {}) {
	aktuellerBedarfID = bedarfID;
	neuerTag = null;
	bearbeiten = false;
	letzterBericht = null;
	onChangeCallback = onChange;
	onEventOeffnenCallback = onEventOeffnen;
	await ladeStammdaten();
	baueModalHuelle();
	await rendereInhalt();
}

/**
 * Öffnet den Bedarf-Dialog im Anlegen-Modus für einen Tag.
 * @param {string} tag YYYY-MM-DD
 * @param {function} onChange (aenderung) => void nach Änderungen — aenderung beschreibt, was sich geändert
 *   hat (siehe nachAktion bzw. { art: "bedarf", schichtenGeaendert }), damit die Ansicht gezielt aktualisieren kann
 */
async function oeffneNeuenBedarf(tag, onChange) {
	aktuellerBedarfID = null;
	neuerTag = tag;
	bearbeiten = true;
	letzterBericht = null;
	onChangeCallback = onChange;
	onEventOeffnenCallback = null;
	await ladeStammdaten();
	entwurf = erstelleEntwurf(null);
	baueModalHuelle();
	await rendereInhalt();
}

export { oeffneBedarfZuordnung, oeffneNeuenBedarf };
